import { DialogTitleWithCloseButton, TextField } from '@carsys/ui'
import { useNotifier } from '@cbgms/base/notifier'
import { Button, Dialog, DialogActions, DialogContent, Divider, Grid } from '@mui/material'
import { addUpdateKeyloopConnection, fetchKeyloopCalled } from 'app/auth/state/actions'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useGetCurrentShopID } from 'app/shops/hooks/useGetCurrentShopID'
import { IRootReducerState } from 'state/rootReducer'
import Delete from '@mui/icons-material/Delete'

export interface IKeyloopLoginProps {
  open: boolean
  onCancel: () => void
  onReset: () => void
}

export const KeyloopLogin = (props: IKeyloopLoginProps) => {
  const companyUuid = useSelector((state: IRootReducerState) => state.auth?.user?.IdToken['custom:company_uuid'])
  const shopID = useGetCurrentShopID()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const [baseUrl, setBaseUrl] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [baseUrlError, setBaseUrlError] = useState(false)
  const [baseUrlHelperText, setBaseUrlHelperText] = useState('')
  const [clientIdError, setClientIdError] = useState(false)
  const [clientIdHelperText, setClientIdHelperText] = useState('')
  const [ClientSecretError, setClientSecretError] = useState(false)
  const [ClientSecretHelperText, setClientSecretHelperText] = useState('')
  const [connectedFlag, setConnectedFlag] = useState(false)

  const closeModel = () => {
    props.onCancel()
    reset()
  }

  const fetchKeyloopConnection = useCallback(async () => {
    try {
      const data = await dispatch(fetchKeyloopCalled(companyUuid, shopID))
      if (data.Data && 'URL' in data.Data && 'UserID' in data.Data && 'Password' in data.Data) {
        const keyloopData = data.Data as { URL: string; UserID: string; Password: string }
        setBaseUrl(keyloopData.URL)
        setClientId(keyloopData.UserID)
        setClientSecret(keyloopData.Password)
        setConnectedFlag(true)
      } else {
        setConnectedFlag(false)
      }
    } catch (error) {
      notifier.error(t('onboarding.capture_gms_information.errorFetchKeyloop'))
    }
  }, [dispatch, notifier])

  useEffect(() => {
    reset()
    fetchKeyloopConnection()
  }, [fetchKeyloopConnection, props.open])

  const reset = () => {
    setBaseUrl('')
    setClientId('')
    setClientSecret('')
    setBaseUrlError(false)
    setClientIdError(false)
    setClientSecretError(false)
    setBaseUrlHelperText('')
    setClientIdHelperText('')
    setClientSecretHelperText('')
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    if (name === 'BaseUrl') {
      if (value === '') {
        setBaseUrlError(true)
        setBaseUrlHelperText(t('onboarding.capture_gms_information.keyloop_baseUrl_err'))
      } else {
        setBaseUrlError(false)
        setBaseUrlHelperText('')
      }
      setBaseUrl(value)
    }
    if (name === 'ClientId') {
      if (value === '') {
        setClientIdError(true)
        setClientIdHelperText(t('onboarding.capture_gms_information.keyloop_clientId_err'))
      } else {
        setClientIdError(false)
        setClientIdHelperText('')
      }
      setClientId(value)
    }
    if (name === 'ClientSecret') {
      if (value === '') {
        setClientSecretError(true)
        setClientSecretHelperText(t('onboarding.capture_gms_information.keyloop_client_Secret_err'))
      } else {
        setClientSecretError(false)
        setClientSecretHelperText('')
      }
      setClientSecret(value)
    }
  }

  const handleOnConfirm = async () => {
    const payload = {
      CompanyUUID: companyUuid,
      ShopID: shopID,
      UserID: clientId,
      Password: clientSecret,
      URL: baseUrl
    }
    try {
      const { Data }: { Data: unknown } = await dispatch(addUpdateKeyloopConnection(payload))
      const data = Data as { Status: string; Message: string }
      if (data.Status === 'Success')
        notifier.success(
          connectedFlag
            ? t('onboarding.capture_gms_information.update_keyloop_success')
            : t('onboarding.capture_gms_information.add_keyloop_success')
        )
    } catch (err) {
      notifier.error(
        connectedFlag
          ? t('onboarding.capture_gms_information.failed_to_update_keyloop')
          : t('onboarding.capture_gms_information.failed_to_add_keyloop')
      )
    } finally {
      closeModel()
    }
  }

  const handleResetConnection = async () => {
    props.onReset()
  }

  return (
    <Dialog data-testid='confirm-dialog-title' open={props.open} onClose={closeModel} fullWidth>
      <DialogTitleWithCloseButton onClose={closeModel}>
        {t('onboarding.capture_gms_information.keyloop_field_title')}
      </DialogTitleWithCloseButton>
      <Divider sx={{ my: 1 }} />
      <DialogContent sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems='flex-start'>
          <Grid item xs={8}>
            <TextField
              required
              label={t('onboarding.capture_gms_information.keyloop_baseUrl')}
              inputProps={{
                onChange: (event: any) => {
                  handleChange(event)
                }
              }}
              name='BaseUrl'
              value={baseUrl}
              error={baseUrlError}
              helperText={baseUrlHelperText}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              required
              label={t('onboarding.capture_gms_information.keyloop_clientId')}
              inputProps={{
                onChange: (event: any) => {
                  handleChange(event)
                }
              }}
              name='ClientId'
              value={clientId}
              error={clientIdError}
              helperText={clientIdHelperText}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              required
              label={t('onboarding.capture_gms_information.keyloop_client_Secret')}
              inputProps={{
                onChange: (event: any) => {
                  handleChange(event)
                }
              }}
              name='ClientSecret'
              type='password'
              autoComplete='current-password'
              value={clientSecret}
              error={ClientSecretError}
              helperText={ClientSecretHelperText}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <DialogActions>
          <Button color='primary' startIcon={<Delete />} variant='contained' onClick={handleResetConnection} disabled={!connectedFlag}>
            {t('global.remove')}
          </Button>
          <Button variant='outlined' color='primary' onClick={closeModel}>
            {t('global.cancel')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleOnConfirm}
            disabled={baseUrl === '' || clientId === '' || clientSecret === ''}
          >
            {connectedFlag ? t('global.update') : t('Alert.Save')}
          </Button>
        </DialogActions>
      </DialogActions>
    </Dialog>
  )
}
export default KeyloopLogin
